import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Ticket, 
  Calendar, 
  MapPin, 
  ArrowLeft, 
  Loader2,
  Mail,
  User,
  Phone,
  Store,
  CreditCard,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

export default function GuestCheckout() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const ticketTypeId = searchParams.get('ticket');
  const quantity = parseInt(searchParams.get('qty') || '1');
  
  const [event, setEvent] = useState(null);
  const [ticketType, setTicketType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Guest info
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  // Vendor name — buyer-entered per-order; only rendered when the event is
  // opted into vendor-name collection. Sent to backend which sanitizes and
  // attaches to the payment gateway metadata + transaction record.
  const [vendorName, setVendorName] = useState('');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events/${eventId}`);
        const rawEvent = response.data;
        const allTypes = Array.isArray(rawEvent.ticket_types) ? rawEvent.ticket_types : [];

        // Resolve the exact ticket type the buyer is checking out.
        const selectedTicket = ticketTypeId
          ? allTypes.find((t) => t.ticket_id === ticketTypeId)
          : allTypes[0];

        // Trim event.ticket_types down to just the selected one before
        // committing to React state. The checkout page only ever renders one
        // ticket type — the rest of the array is dead weight in memory.
        //
        // Why this matters (root cause of the iOS Safari scroll freeze on
        // events with many ticket types, observed on evt_d1741bf4a980 which
        // has 34 types): iOS Safari's touch-scroll gesture recognizer is
        // measurably more sensitive to main-thread jitter than Blink.
        // Keeping a large ticket_types array in React state pulls extra
        // reconciliation work into the same tick where PostHog rrweb runs
        // its ~3-5s post-mount snapshot cycle, pushing past the threshold
        // where WebKit's native touch-scroll arms. Other events (max 11
        // types tested) stay under the threshold. Trimming to 1 item
        // normalizes the runtime footprint across all events without any
        // visible behavior change.
        setEvent({
          ...rawEvent,
          ticket_types: selectedTicket ? [selectedTicket] : [],
        });
        setTicketType(selectedTicket);
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Event not found');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, ticketTypeId, navigate]);

  // ── Session recording is disabled globally in posthog.init (public/index.html) ──
  // Pageview/event analytics remain active; no rrweb session replay runs.

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!guestEmail || !guestEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    // Vendor name required only when the organizer has enabled collection.
    // Backend also enforces this — this is a UX-friendly early check.
    if (event?.collect_vendor_name === true && !vendorName.trim()) {
      toast.error('Please enter the vendor / store name');
      return;
    }
    if (!ticketType) {
      toast.error('No ticket selected');
      return;
    }
    
    setProcessing(true);
    
    try {
      const totalAmount = ticketType.price * quantity;
      
      if (ticketType.is_free || totalAmount === 0) {
        // Free ticket - RSVP
        const response = await axios.post(`${API_URL}/api/tickets/guest/rsvp`, {
          event_id: eventId,
          ticket_type_id: ticketType.ticket_id,
          quantity: quantity,
          guest_info: {
            email: guestEmail.toLowerCase(),
            name: guestName,
            phone: guestPhone || null
          }
        });
        
        toast.success('Ticket confirmed! Check your email for your QR code.');
        navigate(`/my-tickets/guest?email=${encodeURIComponent(guestEmail.toLowerCase())}`);
      } else {
        // Paid ticket - Initialize payment
        const response = await axios.post(`${API_URL}/api/tickets/guest/payment/initialize`, {
          event_id: eventId,
          tickets: [{ ticket_type_id: ticketType.ticket_id, quantity: quantity }],
          total_amount: totalAmount,
          guest_info: {
            email: guestEmail.toLowerCase(),
            name: guestName,
            phone: guestPhone || null
          },
          // Only include vendor_name when the event is collecting it. Sending
          // it on a non-collecting event triggers a 400 (spec-mandated reject).
          ...(event?.collect_vendor_name === true
            ? { vendor_name: vendorName.trim() }
            : {})
        });
        
        // Redirect to Paystack
        if (response.data.authorization_url) {
          window.location.href = response.data.authorization_url;
        } else {
          toast.error('Payment initialization failed');
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      return format(new Date(dateStr), 'EEE, MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!event || !ticketType) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Event not found</h1>
          <Link to="/events">
            <Button>Browse Events</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const totalAmount = ticketType.price * quantity;
  const isFree = ticketType.is_free || totalAmount === 0;

  return (
    <Layout showFooter={false}>
      <div
        className="max-w-4xl mx-auto px-4 md:px-8 py-8"
        data-testid="guest-checkout-page"
        style={{
          // Extra bottom breathing room so the Pay button is never clipped by
          // Safari iOS's dynamic bottom toolbar when it expands (URL bar +
          // tab switcher come back after a period of scroll inactivity).
          // Stacks with Layout's <main> paddingBottom (80px MobileNav
          // clearance + safe-area-inset-bottom home indicator inset).
          paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Back Button */}
        <Link
          to={`/events/${eventId}`}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Event
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div>
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Guest Checkout
                </CardTitle>
                <CardDescription>
                  Enter your details to receive your ticket via email
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCheckout} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="name"
                        type="text"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder="Enter your full name"
                        className="pl-10"
                        required
                        data-testid="checkout-name-input"
                      />
                    </div>
                  </div>

                  {event?.collect_vendor_name === true && (
                    <div className="space-y-2" data-testid="checkout-vendor-name-block">
                      <Label htmlFor="vendor-name">Vendor / Store Name *</Label>
                      <div className="relative">
                        <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="vendor-name"
                          type="text"
                          value={vendorName}
                          onChange={(e) => setVendorName(e.target.value)}
                          placeholder="e.g., Mama Put Kitchen"
                          className="pl-10"
                          maxLength={100}
                          required
                          data-testid="checkout-vendor-name-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Required by the organizer for this event. Used only for
                        organizer reconciliation — never shown on your receipt.
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="pl-10"
                        required
                        data-testid="checkout-email-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your ticket with QR code will be sent here
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="phone"
                        type="tel"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        placeholder="Enter your phone number"
                        className="pl-10"
                        data-testid="checkout-phone-input"
                      />
                    </div>
                  </div>

                  <Separator className="my-6" />
                  
                  <Button
                    type="submit"
                    className="w-full h-12 text-lg gap-2"
                    disabled={processing}
                    data-testid="complete-checkout-btn"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : isFree ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Get Free Ticket
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Pay {CURRENCY_SYMBOLS[event?.currency] || '₦'}{totalAmount.toLocaleString()}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    {isFree 
                      ? 'Your ticket will be sent to your email instantly'
                      : 'You will be redirected to secure payment'
                    }
                  </p>
                </form>
              </CardContent>
            </Card>
            
            {/* Already have account */}
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                Have an account?{' '}
                <Link 
                  to={`/login?redirect=/events/${eventId}`}
                  className="text-primary hover:underline"
                >
                  Sign in for faster checkout
                </Link>
              </p>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="bg-card/50 border-border/50 sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Event Info */}
                <div className="p-4 rounded-xl bg-muted/50">
                  <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                  {event.organizer_name && (
                    <p className="text-xs text-muted-foreground mb-2" data-testid="checkout-organizer-name">
                      Organized by {event.organizer_name}
                    </p>
                  )}
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{event.venue || 'Venue TBA'}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Ticket Details */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{ticketType.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {quantity} × {isFree ? 'Free' : `${CURRENCY_SYMBOLS[event?.currency] || '₦'}${ticketType.price.toLocaleString()}`}
                      </p>
                    </div>
                    <Badge variant="outline">{quantity} ticket{quantity > 1 ? 's' : ''}</Badge>
                  </div>
                </div>

                <Separator />

                {/* Total */}
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span className={isFree ? 'text-green-500' : ''}>
                    {isFree ? 'Free' : `${CURRENCY_SYMBOLS[event?.currency] || '₦'}${totalAmount.toLocaleString()}`}
                  </span>
                </div>

                {/* Security Note */}
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Secure checkout • QR code sent to email
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
